import React, { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import ChipFilterButton from "./button-chip-filter";
import styles from "./button-chip-filter.module.css";
import { getIcon, IconName } from "@/lib/get-icon";

type Size = "sm" | "md" | "lg";

export interface ChipFilterGroupItem {
  id: string;
  label: React.ReactNode;
  value?: string | number;
  disabled?: boolean;
  leadingIcon?: React.ReactNode | IconName;
  trailingIcon?: React.ReactNode | IconName;
}

export interface ChipFilterGroupProps {
  items: ChipFilterGroupItem[];
  defaultValue?: string | number;
  defaultActiveId?: string;
  size?: Size;
  containerClassName?: string;
  className?: string;
  onChange?: (
    value: string | number | undefined,
    item: ChipFilterGroupItem
  ) => void;
}

const ChipFilterGroup: React.FC<ChipFilterGroupProps> = ({
  items,
  size = "md",
  defaultValue,
  defaultActiveId,
  containerClassName,
  className,
  onChange,
}) => {
  // Find initial active ID either from defaultValue or defaultActiveId
  const initialActiveId = defaultValue
    ? items.find((item) => item.value === defaultValue)?.id
    : (defaultActiveId ?? items[0]?.id);

  const [activeId, setActiveId] = useState<string | undefined>(initialActiveId);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleClick = (id: string) => {
    if (!isDragging) {
      setActiveId(id);
      const selectedItem = items.find((item) => item.id === id);
      if (selectedItem) {
        onChange?.(selectedItem.value, selectedItem);
      }
    }
  };

  const checkForGradients = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setShowLeftGradient(scrollLeft > 0);
      setShowRightGradient(scrollLeft < scrollWidth - clientWidth);
    }
  };

  useEffect(() => {
    checkForGradients();
    window.addEventListener("resize", checkForGradients);
    return () => window.removeEventListener("resize", checkForGradients);
  }, []);

  // Update active ID if defaultValue changes
  useEffect(() => {
    if (defaultValue !== undefined) {
      const newActiveId = items.find((item) => item.value === defaultValue)?.id;
      if (newActiveId) {
        setActiveId(newActiveId);
      }
    }
  }, [defaultValue, items]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (containerRef.current?.offsetLeft || 0));
    setScrollLeft(containerRef.current?.scrollLeft || 0);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    if (containerRef.current) {
      const x = e.pageX - (containerRef.current.offsetLeft || 0);
      const walk = (x - startX) * 2;
      containerRef.current.scrollLeft = scrollLeft - walk;
      checkForGradients();
    }
  };

  const handleScroll = () => {
    checkForGradients();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - (containerRef.current?.offsetLeft || 0));
    setScrollLeft(containerRef.current?.scrollLeft || 0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    if (containerRef.current) {
      const x = e.touches[0].pageX - (containerRef.current.offsetLeft || 0);
      const walk = (x - startX) * 2;
      containerRef.current.scrollLeft = scrollLeft - walk;
      checkForGradients();
    }
  };

  return (
    <div
      className={cn(
        "no-scrollbar relative flex w-full overflow-x-hidden",
        containerClassName
      )}
    >
      {showLeftGradient && (
        <div
          className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-16"
          style={{
            background: "linear-gradient(to right, white, transparent)",
          }}
        />
      )}
      <div
        ref={containerRef}
        className={cn(
          "no-scrollbar flex w-fit min-w-full cursor-grab overflow-x-auto overflow-y-hidden p-0.5 active:cursor-grabbing md:min-w-[390px]",
          className
        )}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleMouseUp}
        onTouchMove={handleTouchMove}
        onScroll={handleScroll}
      >
        {items.map((item) => (
          <ChipFilterButton
            key={item.id}
            size={size}
            value={item.value}
            className={cn("text-neutral-500", {
              [styles.active]: activeId === item.id,
            })}
            onClick={() => handleClick(item.id)}
            disabled={item.disabled}
            leadingIcon={
              typeof item.leadingIcon === "string"
                ? getIcon({ name: item.leadingIcon as IconName })
                : item.leadingIcon
            }
            trailingIcon={
              typeof item.trailingIcon === "string"
                ? getIcon({ name: item.trailingIcon as IconName })
                : item.trailingIcon
            }
          >
            {item.label}
          </ChipFilterButton>
        ))}
      </div>
      {showRightGradient && (
        <div
          className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-16"
          style={{
            background: "linear-gradient(to left, white, transparent)",
          }}
        />
      )}
    </div>
  );
};

export default ChipFilterGroup;
